import type { Timestamp } from 'firebase-admin/firestore';
import type { Model, Response } from '../models/models';
import type { WrappedDocumentSnapshot, WrappedTimestamp } from '../providers/client-provider';

export function dateFromTimestamp(
  value?: null | string | Date | WrappedTimestamp | { seconds: number } | { _seconds: number } | number,
  defaultValue: Date | null = null
): Date | null {
  if (!value) {
    return defaultValue;
  }

  if (value && typeof value === 'object') {
    if ('seconds' in (value as { seconds: number })) {
      return new Date((value as { seconds: number }).seconds * 1000);
    }

    if ('_seconds' in (value as { _seconds: number })) {
      return new Date((value as { _seconds: number })._seconds * 1000);
    }
  }

  if (typeof value === 'string') {
    return new Date(value);
  }

  if (typeof value === 'number') {
    return new Date(value * 1000);
  }

  if (typeof value === 'object' && value instanceof Date) {
    return value;
  }

  return (value as Timestamp).toDate();
}

/**
 *
 * @param {WrappedDocumentSnapshot} snap
 * @return {Model | null}
 */
export function toObj<T extends Model>(snap?: WrappedDocumentSnapshot<T | null> | null): T | null {
  if (!snap) {
    return null;
  }

  const data = snap.data();
  if (!data) {
    return null;
  }
  return {
    ...(data as any), // eslint-disable-line
    createdAt: dateFromTimestamp(data.createdAt),
    updatedAt: dateFromTimestamp(data.updatedAt),
    id: data.originalTranslationId ? data.originalTranslationId : snap.id,
    language: data.originalTranslationId ? snap.id : null,
    path: snap.ref.path,
    parentPath: snap.ref.parent.parent?.path || null,
    parentId: snap.ref.parent.parent?.id || null,
  } as T;
}

/**
 *
 * @param {WrappedDocumentSnapshot[]} snaps
 * @return {Model[]}
 */
export function toList<T extends Model>(snaps: WrappedDocumentSnapshot<T>[]): T[] {
  const list: T[] = [];

  snaps.map((snap) => {
    const obj = toObj<T>(snap);
    if (obj) {
      list.push(obj);
    }
  });

  return list;
}

/**
 *
 * @param {Snapshot} snap
 * @return {Response | null}
 */
export function toResponse<T extends Response>(snap: WrappedDocumentSnapshot): T | null {
  const data = snap.data();
  if (!data) {
    return null;
  }

  return {
    ...data,
    createdAt: data && data.createdAt ? (data.createdAt.toDate().toISOString() as string) : null,
    updatedAt: data && data.updatedAt ? (data.updatedAt.toDate().toISOString() as string) : null,
    id: data.originalTranslationId ? data.originalTranslationId : snap.id,
    language: data.originalTranslationId ? snap.id : null,
  } as T;
}

// eslint-disable-next-line
export function removeUndefinedKeys(obj: Record<string, any>) {
  if (typeof obj === 'undefined') {
    return null;
  }
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // eslint-disable-next-line
  const newObj: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      newObj[key] = removeUndefinedKeys(obj[key]);
    } else if (obj[key] === undefined) {
      newObj[key] = null;
    } else {
      newObj[key] = obj[key];
    }
  });
  return newObj;
}

export function splitArrayIntoChunks<T>(arr: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    chunks.push(arr.slice(i, i + chunkSize));
  }
  return chunks;
}

// eslint-disable-next-line
export function deleteUndefinedKeysInPlace(obj: Record<string, any>) {
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      deleteUndefinedKeysInPlace(obj[key]);
    } else if (obj[key] === undefined) {
      delete obj[key];
    }
  });
}
